// Borrowed from https://github.com/okikio/sharedworker/blob/main/src/index.ts
/**
 * indicates if SharedWorker is supported, in the global scope
 */
export var SharedWorkerSupported = 'SharedWorker' in globalThis;
/**
 * A polyfill class for `SharedWorker`, it accepts a URL/string as well as any other options the spec. allows for `SharedWorker`.
 * It supports all the same methods and properties as the original, except it adds compatibility methods and properties for older browsers that don't support `SharedWorker`, so, it can switch to normal `Workers` instead.
 */
var SharedWorkerPolyfill = /** @class */ (function () {
    function SharedWorkerPolyfill(url, opts) {
        if (SharedWorkerSupported) {
            this.ActualWorker = new SharedWorker(url, opts);
        }
        else {
            this.ActualWorker = new Worker(url, opts);
        }
    }
    Object.defineProperty(SharedWorkerPolyfill.prototype, "onmessage", {
        /**
         * An EventListener called when MessageEvent of type message is fired on the port—that is, when the port receives a message.
         */
        get: function () {
            var _a;
            if (SharedWorkerSupported) {
                return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.onmessage;
            }
            return this.ActualWorker.onmessage;
        },
        set: function (value) {
            if (SharedWorkerSupported) {
                this.ActualWorker.port.onmessage = value;
            }
            else {
                this.ActualWorker.onmessage = value;
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SharedWorkerPolyfill.prototype, "onmessageerror", {
        /**
         * An EventListener called when a MessageEvent of type MessageError is fired—that is, when it receives a message that cannot be deserialized.
         */
        get: function () {
            var _a;
            if (SharedWorkerSupported) {
                return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.onmessageerror;
            }
            return this.ActualWorker.onmessageerror;
        },
        set: function (value) {
            if (SharedWorkerSupported) {
                this
                    .ActualWorker.port.onmessageerror = value;
            }
            else {
                this.ActualWorker.onmessageerror = value;
            }
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Starts the sending of messages queued on the port (only needed when using EventTarget.addEventListener; it is implied when using MessagePort.onmessage.)
     */
    SharedWorkerPolyfill.prototype.start = function () {
        var _a;
        if (SharedWorkerSupported) {
            return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.start();
        }
    };
    /**
     * Clones message and transmits it to worker's global environment. transfer can be passed as a list of objects that are to be transferred rather than cloned.
     */
    SharedWorkerPolyfill.prototype.postMessage = function (message, transfer) {
        var _a;
        if (SharedWorkerSupported) {
            return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.postMessage(message, transfer);
        }
        return this.ActualWorker.postMessage(message, transfer);
    };
    /**
     * Immediately terminates the worker. This does not let worker finish its operations; it is halted at once. ServiceWorker instances do not support this method.
     */
    SharedWorkerPolyfill.prototype.terminate = function () {
        var _a;
        if (SharedWorkerSupported) {
            return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.close();
        }
        return this.ActualWorker.terminate();
    };
    /**
     * Disconnects the port, so it is no longer active.
     */
    SharedWorkerPolyfill.prototype.close = function () {
        return this.terminate();
    };
    Object.defineProperty(SharedWorkerPolyfill.prototype, "port", {
        /**
         * Returns a MessagePort object used to communicate with and control the shared worker.
         */
        get: function () {
            return (SharedWorkerSupported
                ? this.ActualWorker.port
                : this.ActualWorker);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SharedWorkerPolyfill.prototype, "onerror", {
        /**
         * Is an EventListener that is called whenever an ErrorEvent of type error event occurs.
         */
        get: function () {
            return this.ActualWorker.onerror;
        },
        set: function (value) {
            this.ActualWorker.onerror = value;
        },
        enumerable: false,
        configurable: true
    });
    SharedWorkerPolyfill.prototype.addEventListener = function (type, listener, options) {
        var _a;
        if (SharedWorkerSupported && type !== 'error') {
            return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.addEventListener(type, listener, options);
        }
        return this.ActualWorker.addEventListener(type, listener, options);
    };
    SharedWorkerPolyfill.prototype.removeEventListener = function (type, listener, options) {
        var _a;
        if (SharedWorkerSupported && type !== 'error') {
            return (_a = this.ActualWorker) === null || _a === void 0 ? void 0 : _a.port.removeEventListener(type, listener, options);
        }
        return this.ActualWorker.removeEventListener(type, listener, options);
    };
    /**
     * Dispatches an event to this EventTarget.
     */
    SharedWorkerPolyfill.prototype.dispatchEvent = function (event) {
        return this.ActualWorker.dispatchEvent(event);
    };
    return SharedWorkerPolyfill;
}());
export { SharedWorkerPolyfill };
export default SharedWorkerPolyfill;
